import { useMemo } from 'react';
import { ArrowStyleTypes } from 'widgets/Lines/common/CommonLine.types';

import { calculatePathBounds, calculateSvgArrowheadLength } from '../LineWidget.helpers';
import { LineRenderProps } from '../LineWidget.types';

/**
 * Custom React hook for rendering lines with specified styles and dimensions.
 *
 * @param {LineRenderProps} props - Object containing properties for line rendering.
 * @returns An object containing the calculated width and height for the SVG container,
 *          and the delta dimensions (difference in width and height from provided dimensions).
 */
export const useLineRender = (props: LineRenderProps) => {
  // Destructures the properties from the props object
  const {
    newPosList,
    strokeWidth,
    startArrowStyle,
    endArrowStyle,
    widthPx = 0,
    heightPx = 0,
    fitSvgToPathSize = true,
  } = props;

  // Memoizes the calculation of path bounds based on the line properties
  const pathBounds = useMemo(() => {
    return calculatePathBounds(newPosList, strokeWidth, startArrowStyle, endArrowStyle);
  }, [newPosList, strokeWidth, startArrowStyle, endArrowStyle]);

  // Determines the SVG container dimensions, ensuring they are not smaller than the path bounds
  const svgWidth = fitSvgToPathSize ? Math.max(widthPx, pathBounds.width) : widthPx;
  const svgHeight = fitSvgToPathSize ? Math.max(heightPx, pathBounds.height) : heightPx;

  const hasArrow = startArrowStyle !== ArrowStyleTypes.none || endArrowStyle !== ArrowStyleTypes.none;
  const arrowThickness = calculateSvgArrowheadLength(strokeWidth);

  const additionalOffset = useMemo(() => {
    if (!fitSvgToPathSize) return { x: 0, y: 0 };

    const minX = Math.min(...newPosList.map((pos) => pos.xPx));
    const minY = Math.min(...newPosList.map((pos) => pos.yPx));
    const firstPos = newPosList[0];
    const lastPos = newPosList[newPosList.length - 1];
    return {
      x:
        minX === firstPos.xPx || minX === lastPos.xPx
          ? 0
          : Math.min(Math.abs(firstPos.xPx - minX), Math.abs(lastPos.xPx - minX)),
      y:
        minY === firstPos.yPx || minY === lastPos.yPx
          ? 0
          : Math.min(Math.abs(firstPos.yPx - minY), Math.abs(lastPos.yPx - minY)),
    };
  }, [newPosList, fitSvgToPathSize]);

  // Memoizes the offset for the SVG container and contents to fit inside svg
  const offset = useMemo(() => {
    return {
      x: fitSvgToPathSize ? additionalOffset.x + strokeWidth + (hasArrow ? arrowThickness / 2 : 0) : 0,
      y: fitSvgToPathSize ? additionalOffset.y + strokeWidth + (hasArrow ? arrowThickness / 2 : 0) : 0,
    };
  }, [strokeWidth, hasArrow, arrowThickness, fitSvgToPathSize, additionalOffset]);

  // Returns the calculated dimensions for use in rendering
  return {
    svgWidth,
    svgHeight,
    offset,
  };
};
